import { makeStaticStyles } from '@fluentui/react-components';
import { themes } from '@bingads-webui-fluent-component/tokens';

import type { FluentV2StaticStyleRules } from './types';

/**
 * Global styles for the PIDL secure-field iframe (PXEnableSecureField* flights).
 *
 * Must be global (NOT scoped under makeStyles) because PidlSDK walks document.styleSheets,
 * matches rules whose selector contains ".pidlddc-secure-property", strips that token,
 * and ships the result into the iframe document via postMessage. Any scope prefix would
 * break the match.
 *
 * NOTE: We can't use `tokens.*` (the standard Fluent import) here even though
 * v2-pidl-overrides.js does. `tokens.colorNeutralForeground1` resolves to the literal
 * string `var(--colorNeutralForeground1)` — a CSS variable reference. That works in the
 * parent document because the FluentProvider defines those variables on a root element.
 * But the secure-field iframe is a separate document (different origin, no FluentProvider),
 * so the shipped `var(...)` references resolve to nothing inside.
 *
 * Instead we import `themes.cozy.light` from the same package — same source of truth, but
 * with literal color/sizing values (because `themes.cozy.light` is built from Fluent's
 * `createLightTheme(...)`, which produces a Theme object containing concrete values rather
 * than var() references). When the SDK ships our rules, those literals work inside the
 * iframe just like a normal stylesheet.
 *
 * `!important` is applied to every property so this rule fully wins against common's
 * basic-look defaults from getCommonPiFormStyles on the pmax-payment page.
 *
 * Light-theme-locked: this file uses cozy.light. If pmax-payment ever needs dark theme
 * support inside the secure field, we'd switch to a runtime resolver that reads
 * getComputedStyle for each Fluent CSS variable.
 */
const t = themes.cozy.light;

const pidlSecureFieldGlobalStyles = {
  'input[type=text].pidlddc-secure-property': {
    fontFamily: `${t.fontFamilyBase} !important`,
    fontSize: `${t.fontSizeBase300} !important`,
    lineHeight: `${t.lineHeightBase300} !important`,
    color: `${t.colorNeutralForeground1} !important`,
    backgroundColor: `${t.colorNeutralBackground1} !important`,
    borderTopWidth: `${t.strokeWidthThin} !important`,
    borderRightWidth: `${t.strokeWidthThin} !important`,
    borderBottomWidth: `${t.strokeWidthThin} !important`,
    borderLeftWidth: `${t.strokeWidthThin} !important`,
    borderTopStyle: 'solid !important',
    borderRightStyle: 'solid !important',
    borderBottomStyle: 'solid !important',
    borderLeftStyle: 'solid !important',
    borderRadius: `${t.borderRadiusMedium} !important`,
    height: `${t.spacingVerticalXXXL} !important`,
    margin: '0 !important',
    boxSizing: 'border-box !important',
    boxShadow: 'none !important',
    outlineStyle: 'none !important',
    transitionProperty: 'none !important',
    WebkitAppearance: 'none !important',
    appearance: 'none !important',
    borderTopColor: `${t.colorNeutralStroke1} !important`,
    borderRightColor: `${t.colorNeutralStroke1} !important`,
    borderLeftColor: `${t.colorNeutralStroke1} !important`,
    borderBottomColor: `${t.colorNeutralStrokeAccessible} !important`,
  },
  'input[type=text].pidlddc-secure-property:hover': {
    borderTopColor: `${t.colorNeutralStroke1Hover} !important`,
    borderRightColor: `${t.colorNeutralStroke1Hover} !important`,
    borderLeftColor: `${t.colorNeutralStroke1Hover} !important`,
    borderBottomColor: `${t.colorNeutralStrokeAccessible} !important`,
  },
  // The secure-field input inside the iframe has id="secure-field". The iframe's own
  // stylesheet may define a #secure-field:focus rule (specificity 1,1,0) — `!important`
  // overrides regardless of specificity, and the explicit outline removal kills any
  // browser default focus ring.
  'input[type=text].pidlddc-secure-property:focus': {
    borderTopColor: `${t.colorNeutralStroke1} !important`,
    borderRightColor: `${t.colorNeutralStroke1} !important`,
    borderLeftColor: `${t.colorNeutralStroke1} !important`,
    borderBottomColor: `${t.colorCompoundBrandStroke} !important`,
    borderBottomWidth: `${t.strokeWidthThick} !important`,
    outline: 'none !important',
  },
  // Validation error — mirrors the .pidlddc-errorstroke override in v2-pidl-overrides.js.
  // PidlSDK ships any rule whose selector contains ".pidlddc-errorstroke" into the iframe
  // verbatim (no token stripping), so the input there picks it up when the SDK adds the
  // class on validation failure.
  'input[type=text].pidlddc-errorstroke, input[type=text].pidlddc-errorstroke:focus': {
    borderBottomColor: `${t.colorPaletteRedBorder2} !important`,
  },
  // Iframe element sizing — these apply to the iframe element in the parent document (no
  // SDK extraction). The SDK adds .pidlddc-secure-property class to the iframe element.
  'iframe.pidlddc-secure-property': {
    width: '100%',
    height: t.spacingVerticalXXXL,
    verticalAlign: 'middle',
  },
  // CVV-specific iframe override — narrower (50%) with right margin so the help-icon
  // image sits beside it. Width/margin are layout choices (not theme tokens).
  'iframe#pidlddc-secure-input-cvvToken': {
    width: '50%',
    height: t.spacingVerticalXXXL,
    marginRight: '16px',
    verticalAlign: 'middle',
  },
} as unknown as FluentV2StaticStyleRules;

export const usePidlSecureFieldGlobals = makeStaticStyles(pidlSecureFieldGlobalStyles);
